/**
 * Phone Number Normalization
 *
 * Strict E.164 normalization using libphonenumber-js for healthcare applications.
 * Rejects short codes, alphanumeric senders, and invalid numbers.
 */
import { parsePhoneNumber as libphonenumberParse, } from "libphonenumber-js";
/**
 * Normalize a phone number to E.164 format with strict validation
 *
 * @param phone - Phone number in any format
 * @param defaultCountry - ISO 3166-1 alpha-2 country code (default: "US")
 * @returns E.164 formatted phone number (e.g., "+15551234567")
 * @throws Error if phone is invalid, short code, or alphanumeric
 */
export function normalizePhoneNumber(phone, defaultCountry = "US") {
    if (!phone || typeof phone !== "string") {
        throw new Error("Phone number is required and must be a string");
    }
    const trimmed = phone.trim();
    if (trimmed.length === 0) {
        throw new Error("Phone number cannot be empty");
    }
    // Reject alphanumeric senders (common in SMS but not valid phone numbers)
    if (/[a-zA-Z]/.test(trimmed)) {
        throw new Error("Alphanumeric senders are not supported");
    }
    let parsed;
    try {
        parsed = libphonenumberParse(trimmed, defaultCountry);
    }
    catch (error) {
        throw new Error(`Failed to parse phone number: ${error instanceof Error ? error.message : String(error)}`, { cause: error });
    }
    if (!parsed) {
        throw new Error("Phone number could not be parsed");
    }
    // Validate the parsed number
    if (!parsed.isValid()) {
        throw new Error("Phone number is not valid");
    }
    // Reject short codes (typically 5-6 digits)
    const nationalNumber = parsed.nationalNumber;
    if (nationalNumber.length < 7) {
        throw new Error("Short codes are not supported");
    }
    // Get E.164 format
    const e164 = parsed.format("E.164");
    if (!e164.startsWith("+")) {
        throw new Error("Failed to format phone number in E.164");
    }
    return e164;
}
/**
 * Check if a string is a valid phone number
 *
 * @param phone - Phone number in any format
 * @param defaultCountry - ISO 3166-1 alpha-2 country code (default: "US")
 * @returns true if valid phone number, false otherwise
 */
export function isValidPhoneNumber(phone, defaultCountry = "US") {
    try {
        normalizePhoneNumber(phone, defaultCountry);
        return true;
    }
    catch {
        return false;
    }
}
/**
 * Parse a phone number and return detailed information
 *
 * @param phone - Phone number in any format
 * @param defaultCountry - ISO 3166-1 alpha-2 country code (default: "US")
 * @returns Parsed phone number object or null if invalid
 */
export function parsePhoneNumber(phone, defaultCountry = "US") {
    try {
        const trimmed = phone.trim();
        const parsed = libphonenumberParse(trimmed, defaultCountry);
        if (!parsed || !parsed.isValid()) {
            return null;
        }
        // Reject short codes
        if (parsed.nationalNumber.length < 7) {
            return null;
        }
        return {
            e164: parsed.format("E.164"),
            country: parsed.country || defaultCountry,
            nationalNumber: parsed.nationalNumber,
            type: parsed.getType(),
            isValid: true,
        };
    }
    catch {
        return null;
    }
}
/**
 * Normalize multiple phone numbers, skipping invalid ones
 *
 * @param phones - Array of phone numbers
 * @param defaultCountry - ISO 3166-1 alpha-2 country code (default: "US")
 * @returns Array of E.164 formatted phone numbers (invalid ones are filtered out)
 */
export function normalizePhoneNumbers(phones, defaultCountry = "US") {
    return phones
        .map((phone) => {
        try {
            return normalizePhoneNumber(phone, defaultCountry);
        }
        catch {
            return null;
        }
    })
        .filter((phone) => phone !== null);
}
//# sourceMappingURL=normalize.js.map